/**
 * tokenStore — in-memory holder for the JWT access token.
 *
 * Why this exists:
 *   - The access token lives ONLY in this module-level variable. The
 *     refresh token lives in an httpOnly cookie (server-set) that JS can't
 *     read. On page reload, the app calls /api/auth/refresh — the cookie
 *     attaches automatically, the server returns a fresh access token,
 *     and we call setAccessToken() to hydrate.
 *   - Phase 5 (complete): All legacy localStorage token mirrors have been
 *     removed. There is no XSS-readable copy of the access or refresh token.
 *
 * Subscribers can listen for changes (e.g. SyncService picks up logout).
 *
 * NOTE: This is intentionally a tiny module with zero React dependency so
 * non-component code (services, axios interceptors, WS clients) can read it.
 */
let accessToken = null;
const subscribers = new Set();

// One-time scrub: nuke any stale token mirrors left behind by pre-Phase-5
// builds. This protects existing users from carrying around an XSS-readable
// access token in localStorage even though current code never writes one.
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem('maidly_auth_token');
    localStorage.removeItem('maidly_token');
    localStorage.removeItem('maidly_refresh_token');
  }
} catch (e) {
  // Private mode or storage disabled — safe to ignore. Don't even console.log
  // here: this runs on every page load and the disabled-storage case is the
  // common path for Safari private-mode users.
  void e;
}

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token || null;
  subscribers.forEach((fn) => {
    try { fn(accessToken); } catch (e) {
      // One bad subscriber must not break token-change notifications for others.
      console.warn('[tokenStore] subscriber threw on token change', e);
    }
  });
}

export function clearAccessToken() {
  setAccessToken(null);
}

export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Build the Authorization header value, or null if signed out. */
export function getAuthHeader() {
  return accessToken ? `Bearer ${accessToken}` : null;
}

const tokenStore = {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  subscribe,
  getAuthHeader,
};
export default tokenStore;
